import { orderStartedPickup } from './ManifestGrid/utils';

// Actions `receive` creates. Attempt actions are excluded — those carry NOT_DELIVERED in normal
// operation, so treating one as un-received would mark a failed delivery as received.
const RECEIVE_MARKER_ACTIONS = ['AWAITING:RECEIVED', 'MM_CD_ARRIVED:CONFIRMED'];

// Rows a re-receive has to restore. `receive` can't regenerate them: un-receive left the action in
// place, so event_state still reads RECEIVING.
export const unreceivedMarkers = (order) =>
    (order?.wh_events || []).filter(
        (event) => event?.event_id && RECEIVE_MARKER_ACTIONS.includes(event.action) && event.status !== 'RECEIVED'
    );

// Orders that come back to the warehouse off a route — a failed delivery (WAREHOUSE_RETURN) or a route
// return (CD_COMPLETE/END) — have no transition into a receive. The receive marks the event the order
// is sitting on rather than inserting one.
export const receivesInPlace = (order) =>
    order?.event_state === 'WAREHOUSE_RETURN' ||
    (order?.order_type === 'return' && ['CD_COMPLETE', 'END'].includes(order?.event_state));

export const inPlaceReceiveEvents = (order, received_date) => {
    if (!receivesInPlace(order)) return [];

    const current = (order.wh_events || []).findLast(
        (event) => event?.event_id && event.transition?.next === order.event_state
    );
    if (!current || current.status === 'RECEIVED') return [];

    return [
        {
            event_id: current.event_id,
            order_id: current.order_id || order.order_id,
            action: current.action,
            status: 'RECEIVED',
            received_date,
        },
    ];
};

// Only the events a receive writes. Pickup and delivery events also carry status RECEIVED, so status
// alone would patch a delivery to NOT_DELIVERED.
const isReceiveMarker = (event) =>
    RECEIVE_MARKER_ACTIONS.includes(event?.action) || event?.transition?.next === 'WAREHOUSE_RETURN';

// Rows an un-receive has to clear. Orders past pickup are skipped — their receive is settled history.
export const receivedEventIds = (orders = []) => {
    const ids = new Set();

    (orders || []).forEach((order) => {
        if (!order || orderStartedPickup(order)) return;
        (order.wh_events || []).forEach((event) => {
            if (event?.event_id && event.status === 'RECEIVED' && isReceiveMarker(event)) ids.add(event.event_id);
        });
    });

    return [...ids];
};

// Collapse floating-point noise (0.9999999999999999 → 1) and cap display at 2 decimal places,
// dropping trailing zeros so integers stay integers (59.00 → 59, 54.666… → 54.67).
export const formatQty = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Number(n.toFixed(2));
};

// A "line" groups the manifest's items by SKU across orders (null-SKU items each count as
// their own line); a "unit" is a physical piece, so item rows contribute their quantity.
export const getManifestLineCounts = (orders = []) => {
    const lines = new Set();
    let units = 0;

    (orders || []).forEach((order) => {
        (order.manifestItems || []).forEach((item) => {
            lines.add(item.sku || item.item_id);
            units += item.quantity || 1;
        });
    });

    return { lines: lines.size, units: formatQty(units) };
};

export const getManifestShipperNames = (orders = []) => {
    const names = new Set();

    (orders || []).forEach((order) => {
        const name = order?.order_shipper?.business_name;
        if (name) names.add(name);
    });

    return [...names];
};
